import React, {useState, useEffect, useMemo} from 'react'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Drawer,
	IconButton,
	InputAdornment,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import {History, Refresh, Search, Warning} from "@mui/icons-material"
import Grid                                from "@mui/material/Unstable_Grid2"
import {doGet, doPost, doPut}              from "../../util/do-fetch"
import HoverTableRow                       from "../HoverTableRow"
import LeftRight                           from "../LeftRight"
import ConfigVersionHistory                from "./ConfigVersionHistory"

/*
 * App Config (013).
 *
 * The screen used to edit every value inline in the table, which meant a 900-character prompt was
 * rendered in full in a table cell: the list was unreadable and unscannable, and finding a key
 * meant scrolling past the prompts above it. Now the table is a scannable index -- key, what it
 * does, a one-line preview of the value -- and editing happens in a drawer with room for the text.
 *
 * Descriptions come from `config.description` (013) rather than the code, so they can be corrected
 * by an admin without a deploy. A key with no description still lists and still edits; it just
 * says so, which is the prompt to go and write one.
 */

// line breaks are kept: a prompt should look like a prompt. The column clips it, so length is free
const previewOf = (value) => String(value === null || value === undefined ? '' : value).trim()

const AppConfigManager = () => {
	const [config, setConfig] = useState(null)
	const [saving, setSaving] = useState(false)
	const [reloading, setReloading] = useState(false)
	// the key whose history is open, or null -- one dialog, reused per row
	const [historyKey, setHistoryKey] = useState(null)
	// the row open in the drawer, with its in-progress edit. null when the drawer is closed
	const [draft, setDraft] = useState(null)
	const [query, setQuery] = useState('')

	useEffect(() => {
		fetchAppConfig()
	}, [])

	const fetchAppConfig = async () => {
		try {
			const response = await doGet(`/admin/app-config`)
			const data = await response.json()
			setConfig(data)
		}
		catch (err) {
			console.log(err)
		}
	}

	const initAppConfig = async () => {
		setReloading(true)
		try {
			const response = await doPost(`/admin/app-config/init`)
			const data = await response.json()
			setConfig(data)
		}
		catch (err) {
			console.log(err)
		}
		finally {
			setReloading(false)
		}
	}

	const handleUpdateAppConfig = async (key, newValue) => {
		setSaving(true)
		try {
			const options = {
				body: JSON.stringify({value: newValue})
			}
			const response = await doPut(`/admin/app-config/${key}`, options)
			const data = await response.json()
			setConfig(data)
			setDraft(null)
		}
		catch (err) {
			console.log(err)
		}
		finally {
			setSaving(false)
		}
	}

	/*
	 * Key and description are both searched: an admin looking for "the message after a topic" will
	 * not know it is called TOPIC_REVIEW_PROMPT, which is the reason descriptions exist at all.
	 */
	const visible = useMemo(() => {
		if (!config) return []
		const needle = query.trim().toLowerCase()
		if (!needle) return config
		return config.filter(({key, description}) =>
			String(key).toLowerCase().includes(needle)
			|| String(description || '').toLowerCase().includes(needle))
	}, [config, query])

	const isDirty = draft && draft.editedValue !== (draft.value || '')

	return (
		<Grid container maxWidth="lg" sx={{margin: "0 auto"}}>
			<Grid sm={12}>
				<LeftRight
					left={<h2>App Config</h2>}
					right={<Box>
						<Button disabled={reloading} variant={"outlined"} onClick={initAppConfig}>
							{
								reloading
									? <CircularProgress size={14}/>
									: <Refresh/>
							}
							Reload
						</Button>
					</Box>}
				/>

				<Alert color="warning" icon={<Warning/>} sx={{mb: 2}}>
					Many of these values will take effect immediately when modified, please be cautious making changes.
				</Alert>

				<TextField
					fullWidth
					size="small"
					placeholder="Search by name or description…"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start"><Search fontSize="small"/></InputAdornment>
						),
					}}
					sx={{mb: 2}}
				/>
			</Grid>

			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							{/* one column: each row lays itself out, so the head mirrors that shape */}
							<TableCell sx={{py: 1.5}}>
								<Box display="flex" alignItems="center" gap={1}>
									<Box sx={{flex: 1}}><b>Setting</b></Box>
									<Box sx={{fontSize: '.8rem', color: 'text.secondary'}}><b>Version</b></Box>
								</Box>
								<Box display="flex" gap={2} sx={{mt: .5, fontSize: '.8rem', color: 'text.secondary'}}>
									<Box sx={{width: '50%'}}>What it does</Box>
									<Box sx={{width: '50%'}}>Current value</Box>
								</Box>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{visible.map((row) => {
							const {key, value, description, version, updated_at, updated_by_email} = row
							return (
								<HoverTableRow key={key}
								               hover
								               sx={{cursor: 'pointer'}}
								               onClick={() => setDraft({...row, editedValue: value || ''})}>
									<TableCell sx={{py: 1.5}}>
										{/* the key, with version and history on the far right of the same line */}
										<Box display="flex" alignItems="center" gap={1}>
											<Box sx={{
												flex: 1, fontFamily: 'monospace', fontSize: '.85rem',
												fontWeight: 600, wordBreak: 'break-all',
											}}>
												{key}
											</Box>
											<Box sx={{
												fontSize: '.75rem', color: 'text.secondary',
												whiteSpace: 'nowrap', textAlign: 'right',
											}}>
												v{version || 1}
												{updated_at && ` · ${new Date(updated_at).toLocaleDateString()}`}
												{updated_by_email && ` · ${updated_by_email}`}
											</Box>
											<IconButton size="small"
											            title={`Change history for ${key}`}
											            onClick={(e) => {
												            // the row opens the editor; this opens history instead
												            e.stopPropagation()
												            setHistoryKey(key)
											            }}>
												<History fontSize="small"/>
											</IconButton>
										</Box>

										{/*
										  * Description and value, half each. The description sets the row height;
										  * the value is absolutely positioned inside its half so a long prompt is
										  * clipped to that height instead of stretching the row.
										  */}
										<Box display="flex" gap={2} sx={{mt: .75, alignItems: 'stretch'}}>
											<Box sx={{width: '50%'}}>
												<Typography variant="body2"
												            color={description ? 'text.secondary' : 'text.disabled'}
												            sx={{fontStyle: description ? 'normal' : 'italic'}}>
													{description || 'No description yet.'}
												</Typography>
											</Box>
											<Box sx={{width: '50%', position: 'relative', minHeight: '2.5em'}}>
												<Box sx={{
													position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
													overflow: 'hidden',
													fontFamily: 'monospace', fontSize: '.78rem', lineHeight: 1.5,
													color: 'text.secondary',
													whiteSpace: 'pre-wrap', wordBreak: 'break-word',
												}}>
													{previewOf(value) || <em>(empty)</em>}
												</Box>
											</Box>
										</Box>
									</TableCell>
								</HoverTableRow>
							)
						})}

						{config && visible.length === 0 && (
							<TableRow>
								<TableCell>
									<Typography variant="body2" color="text.secondary" sx={{py: 2}}>
										No settings match “{query}”.
									</Typography>
								</TableCell>
							</TableRow>
						)}

						{!config && (
							<TableRow>
								<TableCell>
									<Box display="flex" alignItems="center" gap={1} sx={{py: 2}}>
										<CircularProgress size={16}/>
										<Typography variant="body2" color="text.secondary">Loading…</Typography>
									</Box>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/*
			  * The editor. A drawer rather than a dialog because these values are long and the list
			  * behind stays visible, which keeps the sense of where you are in it.
			  */}
			<Drawer
				anchor="right"
				open={Boolean(draft)}
				onClose={() => setDraft(null)}
				PaperProps={{sx: {width: {xs: '100%', sm: 640}, display: 'flex', flexDirection: 'column'}}}
			>
				{draft && (
					<>
						<Box sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
							<Box display="flex" alignItems="center" gap={1}>
								<Typography variant="h6" sx={{fontFamily: 'monospace', fontSize: '1rem', flex: 1, wordBreak: 'break-all'}}>
									{draft.key}
								</Typography>
								<Chip size="small" label={`v${draft.version || 1}`}/>
								<IconButton size="small"
								            title={`Change history for ${draft.key}`}
								            onClick={() => setHistoryKey(draft.key)}>
									<History fontSize="small"/>
								</IconButton>
							</Box>
							{(draft.updated_at || draft.updated_by_email) && (
								<Typography variant="caption" color="text.secondary">
									Last changed
									{draft.updated_at && ` ${new Date(draft.updated_at).toLocaleString()}`}
									{draft.updated_by_email && ` by ${draft.updated_by_email}`}
								</Typography>
							)}
						</Box>

						<Box sx={{p: 2, flex: 1, overflowY: 'auto'}}>
							{/* what this setting does, before the box that changes it */}
							<Alert severity="info" icon={false} sx={{mb: 2}}>
								{draft.description || 'No description yet for this setting.'}
							</Alert>

							<TextField
								fullWidth
								multiline
								minRows={10}
								label="Value"
								value={draft.editedValue}
								onChange={(e) => setDraft({...draft, editedValue: e.target.value})}
								InputProps={{sx: {fontFamily: 'monospace', fontSize: '.85rem'}}}
							/>
						</Box>

						<Box display="flex" justifyContent="flex-end" gap={1}
						     sx={{p: 2, borderTop: 1, borderColor: 'divider'}}>
							<Button onClick={() => setDraft(null)}>Cancel</Button>
							<Button variant="contained"
							        disabled={!isDirty || saving}
							        onClick={() => handleUpdateAppConfig(draft.key, draft.editedValue)}>
								{saving ? 'Saving…' : 'Save'}
							</Button>
						</Box>
					</>
				)}
			</Drawer>

			<ConfigVersionHistory configKey={historyKey}
			                      open={Boolean(historyKey)}
			                      onClose={() => setHistoryKey(null)}
			/>
		</Grid>
	)
}

export default AppConfigManager
